import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { type FC, useRef, useState } from 'react';
import { MdSearch } from 'react-icons/md';
import { useDebounceCallback } from 'usehooks-ts';

export type TMediaSearchInputProps = {
  /** Called with the trimmed search text, debounced. Empty string when cleared. */
  onSearch: (value: string) => void;
};

// Local state keeps typing snappy; only the debounced value reaches the query.
export const MediaSearchInput: FC<TMediaSearchInputProps> = ({ onSearch }) => {
  const [value, setValue] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedSearch = useDebounceCallback(onSearch, 300);

  const isExpanded = focused || value.length > 0;

  return (
    <TextField
      size="small"
      placeholder={isExpanded ? 'Search files by name' : ''}
      value={value}
      inputRef={inputRef}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={e => {
        setValue(e.target.value);
        debouncedSearch(e.target.value.trim());
      }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <MdSearch size={20} />
          </InputAdornment>
        ),
      }}
      // Collapsed, the adornment icon eats most of the 44px box, leaving the real <input>
      // almost no clickable area — focus it explicitly so a click anywhere in the box works.
      onClick={() => inputRef.current?.focus()}
      sx={{
        width: isExpanded ? 340 : 44,
        transition: 'width 0.2s ease',
        '& .MuiOutlinedInput-root': {
          bgcolor: isExpanded ? 'transparent' : 'action.hover',
          cursor: isExpanded ? 'text' : 'pointer',
        },
      }}
    />
  );
};
